import { GoogleSignInButton } from '@/auth/GoogleSignInButton';
import { useAuth } from '@/auth/AuthContext';
import { authUrl, hasGoogleClientId } from '@/config/api';
import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Register.css';

export function Register() {
  const navigate = useNavigate();
  const { register, loginWithGoogle } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const googleEnabled = hasGoogleClientId();

  const sendCode = async () => {
    setError(null);
    setNotice(null);
    setSendingCode(true);
    try {
      const res = await fetch(authUrl('email-code'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `email-code ${res.status}`);
      }
      setNotice('验证码已发送，请查看邮箱。');
    } catch (e) {
      setError(e instanceof Error ? e.message : '验证码发送失败');
    } finally {
      setSendingCode(false);
    }
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      await register(username, password, email, verificationCode);
      navigate('/market', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : '注册失败');
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogleCredential = useCallback(
    async (credential: string) => {
      setError(null);
      setGoogleSubmitting(true);
      try {
        await loginWithGoogle(credential);
        navigate('/market', { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Google 注册失败');
      } finally {
        setGoogleSubmitting(false);
      }
    },
    [loginWithGoogle, navigate]
  );

  return (
    <div className="register-page">
      <form className="register-card" onSubmit={(e) => void onSubmit(e)}>
        <Link to="/landing" className="register-brand">
          <span>A</span>
          ArbX
        </Link>
        <div>
          <p className="register-kicker">Private trading access</p>
          <h2>套利账户 · 注册</h2>
        </div>

        <label>
          用户名
          <input value={username} onChange={(e) => setUsername(e.target.value)} required />
        </label>
        <label>
          邮箱
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label>
          邮箱验证码
          <div className="register-code-row">
            <input
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              required
              inputMode="numeric"
              maxLength={16}
            />
            <button
              type="button"
              className="register-code-button"
              disabled={submitting || sendingCode || googleSubmitting || !email.trim()}
              onClick={() => void sendCode()}
            >
              {sendingCode ? '发送中…' : '发送验证码'}
            </button>
          </div>
        </label>
        <label>
          密码（至少 8 位）
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </label>
        {notice ? <p className="register-notice">{notice}</p> : null}
        {error ? <p className="register-error">{error}</p> : null}
        <button type="submit" disabled={submitting || googleSubmitting}>
          {submitting ? '注册中…' : '注册并登录'}
        </button>
        {googleEnabled ? (
          <>
            <div className="register-divider">
              <span>或</span>
            </div>
            <GoogleSignInButton
              disabled={submitting || googleSubmitting}
              text="signup_with"
              onCredential={(credential) => void onGoogleCredential(credential)}
              onError={setError}
            />
          </>
        ) : null}
        <p className="register-footer">
          已有账号？<Link to="/login">登录</Link>
          <br />
          <Link to="/landing">返回产品介绍</Link>
        </p>
      </form>
    </div>
  );
}
